import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Step } from '../../types';
import { Tooltip } from '../Tooltip';

export type ScrollIndicatorProps = {
  step: number;
  of: number;
  maxStepReached: number | null;
  steps?: Step[];
  stepNames?: string[];
  baseUrl?: string;
  disabled?: boolean;
};

export const ScrollIndicator: FC<ScrollIndicatorProps> = ({
  step,
  of,
  maxStepReached,
  steps,
  stepNames,
  baseUrl,
  disabled,
}: ScrollIndicatorProps) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center w-4">
      {[...Array(of)].map((_, index) => {
        const stepNo = index + 1;
        const isReached =
          (maxStepReached && index <= maxStepReached) ||
          (!maxStepReached && index === 0);
        const isActive = step === stepNo;
        const isClickable = !disabled && isReached && steps && baseUrl;

        const dotStyles = [
          ...(function* () {
            yield 'rounded-full transition-all duration-200 ease-in-out';

            if (isActive) {
              yield '__active__ w-2 md:w-4 h-2 md:h-4 m-2 bg-blue-100';
            } else {
              yield 'w-1 h-1 md:w-2 md:h-2 m-2.5 md:m-3';
            }

            if (isClickable) {
              yield '__reached__ cursor-pointer transform hover:scale-150';
              if (!isActive) {
                yield 'bg-gray-700';
              }
            } else {
              if (!isActive) {
                yield 'bg-gray-600';
              }
            }
          })(),
        ].join(' ');

        return (
          <div
            key={stepNo}
            data-key={stepNo}
            data-tip={stepNames ? stepNames[index] : `${stepNo} / ${of}`}
            className={dotStyles}
            data-testid="scrollIndicatorTestChild"
            onClick={() =>
              isClickable && navigate(`${baseUrl}/${steps?.[index]}`)
            }
          />
        );
      })}
      <Tooltip place="right" delayShow={200} />
    </div>
  );
};
